import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  User,
  ChevronDown,
  HelpCircle,
  CalendarDays,
  MoreVertical,
  Plus,
  Minus,
  MapPin,
  Upload,
  Mail,
  AlertCircle,
} from "lucide-react";
import logoAsset from "../assets/3.png";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
const C = {
  primary: "#7f00ff",
  primaryFg: "#ffffff",
  primarySoft: "#f1e6ff",
  bg: "#ffffff",
  fg: "#0f172a",
  muted: "#f1f3f5",
  mutedFg: "#6b7a82",
  border: "#e5e7eb",
  destructive: "#e11d48",
  shellBg: "#f4f5f7",
};

const row: CSSProperties = { display: "flex", alignItems: "center" };
const col: CSSProperties = { display: "flex", flexDirection: "column" };
const grid: CSSProperties = { display: "grid", placeItems: "center" };

/* --------------------------------- Header --------------------------------- */

const STEPS = ["Assessment", "Personal Info", "Choose Plan"] as const;

function StepperHeader({ active }: { active: 0 | 1 | 2 }) {
  return (
    <div style={{ ...row, gap: 0, padding: "16px 20px 8px" }}>
      {STEPS.map((label, i) => {
        const done = i < active;
        const cur = i === active;
        const reached = done || cur;
        return (
          <div key={label} style={{ ...row, flex: i === STEPS.length - 1 ? "0 0 auto" : 1 }}>
            <div style={{ ...col, alignItems: "center", gap: 6, flexShrink: 0 }}>
              <div
                style={{
                  ...grid,
                  height: 22,
                  width: 22,
                  borderRadius: 9999,
                  background: reached ? C.primary : C.bg,
                  border: `2px solid ${reached ? C.primary : C.border}`,
                  color: C.primaryFg,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {done ? <Check size={12} strokeWidth={3} /> : cur ? "" : ""}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: cur ? 700 : 500,
                  color: reached ? C.fg : C.mutedFg,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </div>
            </div>
            {i < STEPS.length - 1 ? (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  margin: "0 8px",
                  marginTop: -18,
                  background: i < active ? C.primary : C.border,
                  borderRadius: 9999,
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function MiniHeader({
  title,
  onBack,
  right,
}: {
  title?: string;
  onBack?: () => void;
  right?: ReactNode;
}) {
  return (
    <div style={{ ...row, justifyContent: "space-between", padding: "14px 16px" }}>
      <button
        onClick={onBack}
        aria-label="Back"
        style={{
          ...grid,
          height: 32,
          width: 32,
          background: "transparent",
          border: "none",
          color: C.fg,
          cursor: "pointer",
          opacity: onBack ? 1 : 0,
        }}
      >
        <ArrowLeft size={20} />
      </button>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.fg }}>{title}</div>
      <div style={{ ...grid, height: 32, width: 32 }}>{right}</div>
    </div>
  );
}

/* ----------------------------- Shared buttons ----------------------------- */

function PrimaryButton({
  children,
  onClick,
  disabled,
  icon = <ArrowRight size={16} />,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 54,
        width: "100%",
        ...row,
        justifyContent: "center",
        gap: 8,
        borderRadius: 9999,
        background: disabled ? C.primarySoft : C.primary,
        color: disabled ? C.primary : C.primaryFg,
        fontSize: 15,
        fontWeight: 600,
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span>{children}</span>
      {icon}
    </button>
  );
}

function OutlineButton({
  children,
  onClick,
  icon,
}: {
  children: ReactNode;
  onClick?: () => void;
  icon?: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 54,
        width: "100%",
        ...row,
        justifyContent: "center",
        gap: 8,
        borderRadius: 9999,
        border: `1px solid ${C.primary}`,
        background: C.bg,
        color: C.primary,
        fontSize: 15,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function AvatarBadge({ size = 72 }: { size?: number }) {
  return (
    <div
      style={{
        ...grid,
        height: size,
        width: size,
        borderRadius: 9999,
        background: C.primarySoft,
        color: C.primary,
        margin: "0 auto",
      }}
    >
      <User size={size * 0.5} strokeWidth={1.75} />
    </div>
  );
}

/* ------------------------------ Form fields ------------------------------- */

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 12.5, fontWeight: 500, color: C.fg, marginBottom: 6 }}>{children}</div>
  );
}

function TextField({
  icon,
  placeholder,
  value,
  onChange,
  trailing,
  type = "text",
}: {
  icon?: ReactNode;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  trailing?: ReactNode;
  type?: string;
}) {
  return (
    <div
      style={{
        ...row,
        gap: 8,
        height: 48,
        borderRadius: 12,
        border: `1px solid ${C.border}`,
        padding: "0 12px",
        background: C.bg,
      }}
    >
      {icon ? <span style={{ ...grid, color: C.mutedFg }}>{icon}</span> : null}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          height: "100%",
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: 14,
          color: C.fg,
        }}
      />
      {trailing ? <span style={{ ...grid, color: C.mutedFg }}>{trailing}</span> : null}
    </div>
  );
}

function SelectField({
  icon,
  placeholder,
  value,
  onClick,
  trailing = <ChevronDown size={16} />,
}: {
  icon?: ReactNode;
  placeholder?: string;
  value?: string;
  onClick?: () => void;
  trailing?: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        ...row,
        gap: 8,
        height: 48,
        width: "100%",
        borderRadius: 12,
        border: `1px solid ${C.border}`,
        padding: "0 12px",
        background: C.bg,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      {icon ? <span style={{ ...grid, color: C.mutedFg }}>{icon}</span> : null}
      <span style={{ flex: 1, fontSize: 14, color: value ? C.fg : C.mutedFg }}>
        {value ?? placeholder}
      </span>
      <span style={{ ...grid, color: C.mutedFg }}>{trailing}</span>
    </button>
  );
}

function SectionTitle({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div style={{ ...row, gap: 8, padding: "12px 0", color: C.fg }}>
      <span style={{ ...grid, color: C.mutedFg }}>{icon}</span>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{children}</span>
    </div>
  );
}

function ValidationText({ message }: { message?: string }) {
  if (!message) return null;
  return <p style={{ margin: "6px 2px 0", fontSize: 12, color: C.destructive }}>{message}</p>;
}

/* ============================= Screen 1 ================================== */

function ScreenIntro({ onNext, onHelp }: { onNext: () => void; onHelp: () => void }) {
  return (
    <div style={{ ...col, flex: 1 }}>
      <StepperHeader active={1} />
      <div style={{ ...col, flex: 1, justifyContent: "center", padding: "0 24px", gap: 28 }}>
        <div style={{ ...col, alignItems: "center", gap: 12 }}>
       
           <img style = {{height: 86, width: 86, borderRadius: 16}} src={logoAsset} alt="Logo" />

        </div>
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.fg, margin: 0, lineHeight: 1.2 }}>
            Let's Set Up Your Profile
            <br />& Security
          </h1>
          <p style={{ marginTop: 12, fontSize: 13, color: C.mutedFg, lineHeight: 1.45 }}>
            Your health security is very important, and we
            <br />
            don't sell it to anyone.
          </p>
        </div>
        <div style={{ ...col, gap: 12 }}>
          <PrimaryButton onClick={onNext}>I'm Ready</PrimaryButton>
          <button
            onClick={onHelp}
            style={{
              ...row,
              justifyContent: "center",
              gap: 6,
              background: "transparent",
              border: "none",
              color: C.primary,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              padding: "8px 0",
            }}
          >
            <HelpCircle size={16} /> I need help
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================= Screen 2 ================================== */

type Identity = {
  fullName: string;
  gender: string;
  idCard: string;
  dob: string;
  phone: string;
  phoneCountryIso: string;
  countryIso: string;
  nationality: string;
  street: string;
};

const PHONE_COUNTRY_OPTIONS = [
  { iso: "US", label: "United States", dial: "+1", flag: "🇺🇸" },
  { iso: "GB", label: "United Kingdom", dial: "+44", flag: "🇬🇧" },
  { iso: "IN", label: "India", dial: "+91", flag: "🇮🇳" },
  { iso: "CA", label: "Canada", dial: "+1", flag: "🇨🇦" },
  { iso: "NG", label: "Nigeria", dial: "+234", flag: "🇳🇬" },
  { iso: "KE", label: "Kenya", dial: "+254", flag: "🇰🇪" },
  { iso: "LR", label: "Liberia", dial: "+231", flag: "🇱🇷" },
  { iso: "GH", label: "Ghana", dial: "+233", flag: "🇬🇭" },
  { iso: "ZA", label: "South Africa", dial: "+27", flag: "🇿🇦" },
  { iso: "AU", label: "Australia", dial: "+61", flag: "🇦🇺" },
  { iso: "DE", label: "Germany", dial: "+49", flag: "🇩🇪" },
  { iso: "FR", label: "France", dial: "+33", flag: "🇫🇷" },
  { iso: "JP", label: "Japan", dial: "+81", flag: "🇯🇵" },
  { iso: "BR", label: "Brazil", dial: "+55", flag: "🇧🇷" },
  { iso: "AE", label: "United Arab Emirates", dial: "+971", flag: "🇦🇪" },
  { iso: "SA", label: "Saudi Arabia", dial: "+966", flag: "🇸🇦" },
  { iso: "EG", label: "Egypt", dial: "+20", flag: "🇪🇬" },
  { iso: "PK", label: "Pakistan", dial: "+92", flag: "🇵🇰" },
  { iso: "BD", label: "Bangladesh", dial: "+880", flag: "🇧🇩" },
  { iso: "PH", label: "Philippines", dial: "+63", flag: "🇵🇭" },
] as const;

const COUNTRY_ISO_CODES = [
  "AF","AL","DZ","AD","AO","AG","AR","AM","AU","AT","AZ","BS","BH","BD","BB","BY","BE","BZ","BJ","BT","BO","BA","BW","BR","BN","BG","BF","BI","KH","CM","CA","CV","CF","TD","CL","CN","CO","KM","CG","CR","CI","HR","CU","CY","CZ","CD","DK","DJ","DM","DO","EC","EG","SV","GQ","ER","EE","SZ","ET","FJ","FI","FR","GA","GM","GE","DE","GH","GR","GD","GT","GN","GW","GY","HT","HN","HU","IS","IN","ID","IR","IQ","IE","IL","IT","JM","JP","JO","KZ","KE","KI","KW","KG","LA","LV","LB","LS","LR","LY","LI","LT","LU","MG","MW","MY","MV","ML","MT","MH","MR","MU","MX","FM","MD","MC","MN","ME","MA","MZ","MM","NA","NR","NP","NL","NZ","NI","NE","NG","KP","MK","NO","OM","PK","PW","PA","PG","PY","PE","PH","PL","PT","QA","RO","RU","RW","KN","LC","VC","WS","SM","ST","SA","SN","RS","SC","SL","SG","SK","SI","SB","SO","ZA","KR","SS","ES","LK","SD","SR","SE","CH","SY","TW","TJ","TZ","TH","TL","TG","TO","TT","TN","TR","TM","TV","UG","UA","AE","GB","US","UY","UZ","VU","VA","VE","VN","YE","ZM","ZW"
] as const;

const regionDisplay = typeof Intl !== "undefined" ? new Intl.DisplayNames(["en"], { type: "region" }) : null;

const isoToFlag = (iso: string) =>
  String(iso || "")
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const NATIONALITY_OPTIONS = COUNTRY_ISO_CODES.map((iso) => ({
  iso,
  label: regionDisplay?.of(iso) || iso,
  flag: isoToFlag(iso),
})).sort((a, b) => a.label.localeCompare(b.label));

const GENDER_OPTIONS = ["Female", "Male", "Non-binary", "Prefer not to say", "Other"] as const;

const createSvgAvatar = (label: string, bg: string, fg: string) => {
  const text = (label || "Q").toUpperCase().slice(0, 2);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='256' height='256' viewBox='0 0 256 256'><rect width='256' height='256' rx='128' fill='${bg}'/><text x='50%' y='54%' text-anchor='middle' font-size='96' font-family='Arial, sans-serif' fill='${fg}' font-weight='700'>${text}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const PREMADE_AVATARS = [
  createSvgAvatar("Q", "#7f00ff", "#ffffff"),
  createSvgAvatar("H", "#10b981", "#ffffff"),
  createSvgAvatar("M", "#0ea5e9", "#ffffff"),
  createSvgAvatar("A", "#f59e0b", "#ffffff"),
  createSvgAvatar("C", "#ef4444", "#ffffff"),
  createSvgAvatar("P", "#111827", "#ffffff"),
];

type IdentityFieldErrors = {
  fullName?: string;
  gender?: string;
  dob?: string;
  phone?: string;
  country?: string;
};

function ScreenIdentity({
  data,
  setData,
  province,
  setProvince,
  errors,
  onNext,
  onState,
  onAddress,
  onAvatar,
}: {
  data: Identity;
  setData: (d: Identity) => void;
  province: string;
  setProvince: (v: string) => void;
  errors?: IdentityFieldErrors;
  onNext: () => void;
  onState: () => void;
  onAddress: () => void;
  onAvatar: () => void;
}) {
  const upd = (p: Partial<Identity>) => setData({ ...data, ...p });
  const selectedPhoneCountry = PHONE_COUNTRY_OPTIONS.find((c) => c.iso === data.phoneCountryIso) || PHONE_COUNTRY_OPTIONS[0];
  const selectedNationality = NATIONALITY_OPTIONS.find((c) => c.iso === data.countryIso) || NATIONALITY_OPTIONS[0];
  return (
    <div style={{ ...col, flex: 1 }}>
      <MiniHeader onBack={() => {}} />
      <div style={{ padding: "0 20px 24px", ...col, gap: 14 }}>
        <div style={{ textAlign: "center" }}>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: C.fg,
              margin: 0,
              lineHeight: 1.25,
            }}
          >
            Please confirm and fill
            <br />
            your identity below
          </h1>
          <div style={{ position: "relative", marginTop: 16 }}>
            <AvatarBadge size={64} />
            <button
              type="button"
              onClick={onAvatar}
              aria-label="Set profile image"
              style={{
                ...grid,
                position: "absolute",
                bottom: -2,
                left: "calc(50% + 14px)",
                height: 22,
                width: 22,
                borderRadius: 9999,
                background: C.primary,
                color: C.primaryFg,
                border: `2px solid ${C.bg}`,
                cursor: "pointer",
              }}
            >
              <Plus size={12} strokeWidth={3} />
            </button>
          </div>
        </div>

        <SectionTitle icon={<User size={16} />}>General</SectionTitle>

        <div>
          <FieldLabel>Full Name</FieldLabel>
          <TextField
            icon={<User size={16} />}
            placeholder="Enter your full name..."
            value={data.fullName}
            onChange={(v) => upd({ fullName: v })}
          />
          <ValidationText message={errors?.fullName} />
        </div>

        <div>
          <FieldLabel>Gender</FieldLabel>
          <div
            style={{
              ...row,
              gap: 8,
              height: 48,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              padding: "0 12px",
              background: C.bg,
            }}
          >
            <span style={{ ...grid, color: C.mutedFg }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="10" cy="14" r="5" />
                <path d="M14 10l6-6M16 4h4v4" />
              </svg>
            </span>
            <select
              value={data.gender}
              onChange={(e) => upd({ gender: e.target.value })}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 14,
                color: C.fg,
              }}
            >
              <option value="">Select your gender</option>
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <ValidationText message={errors?.gender} />
        </div>

        <div>
          <FieldLabel>ID Card Number</FieldLabel>
          <TextField
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="12" r="2"/><path d="M14 10h4M14 14h3"/></svg>}
            placeholder="Enter your id card number..."
            value={data.idCard}
            onChange={(v) => upd({ idCard: v })}
            trailing={<HelpCircle size={14} />}
          />
        </div>

        <div>
          <FieldLabel>Date Of Birth</FieldLabel>
          <TextField
            placeholder=""
            value={data.dob}
            onChange={(v) => upd({ dob: v })}
            trailing={<CalendarDays size={16} />}
            type="date"
          />
          <ValidationText message={errors?.dob} />
        </div>

        <div>
          <FieldLabel>Phone Number</FieldLabel>
          <div
            style={{
              ...row,
              gap: 8,
              height: 48,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              padding: "0 12px",
              background: C.bg,
            }}
          >
            <select
              value={data.phoneCountryIso}
              onChange={(e) => {
                const next = PHONE_COUNTRY_OPTIONS.find((c) => c.iso === e.target.value) || PHONE_COUNTRY_OPTIONS[0];
                upd({ phoneCountryIso: next.iso });
              }}
              style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, color: C.fg, maxWidth: 110 }}
            >
              {PHONE_COUNTRY_OPTIONS.map((c) => (
                <option key={c.iso} value={c.iso}>{`${c.flag} ${c.dial}`}</option>
              ))}
            </select>
            <span style={{ color: C.mutedFg, fontSize: 13 }}>{selectedPhoneCountry.dial}</span>
            <input
              value={data.phone}
              onChange={(e) => upd({ phone: e.target.value })}
              placeholder="Enter phone number"
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 14,
                color: C.fg,
              }}
            />
            <HelpCircle size={14} color={C.mutedFg} />
          </div>
          <ValidationText message={errors?.phone} />
        </div>

        <SectionTitle icon={<MapPin size={16} />}>Address</SectionTitle>

        <div>
          <FieldLabel>Nationality</FieldLabel>
          <div
            style={{
              ...row,
              gap: 8,
              height: 48,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              padding: "0 12px",
              background: C.bg,
            }}
          >
            <span style={{ fontSize: 16 }}>{selectedNationality.flag}</span>
            <select
              value={data.countryIso}
              onChange={(e) => {
                const next = NATIONALITY_OPTIONS.find((c) => c.iso === e.target.value) || NATIONALITY_OPTIONS[0];
                upd({ countryIso: next.iso, nationality: next.label });
              }}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 14,
                color: C.fg,
              }}
            >
              {NATIONALITY_OPTIONS.map((c) => (
                <option key={c.iso} value={c.iso}>{c.label}</option>
              ))}
            </select>
            <span style={{ fontSize: 13, color: C.mutedFg, fontWeight: 600 }}>{selectedNationality.iso}</span>
          </div>
          <ValidationText message={errors?.country} />
        </div>

        <div>
          <FieldLabel>State / Province</FieldLabel>
          <TextField
            icon={<MapPin size={16} />}
            placeholder="Type your state or province"
            value={province}
            onChange={setProvince}
          />
        </div>

        <div>
          <FieldLabel>Street Address</FieldLabel>
          <TextField
            icon={<MapPin size={16} />}
            placeholder="Type your street address"
            value={data.street}
            onChange={(v) => upd({ street: v })}
         
          />
        </div>

        <div style={{ paddingTop: 8 }}>
          <PrimaryButton onClick={onNext}>Continue</PrimaryButton>
        </div>

        <button
          onClick={onState}
          style={{
            background: "transparent",
            border: "none",
            color: C.mutedFg,
            fontSize: 12,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Pick a State / Province
        </button>
      </div>
    </div>
  );
}

/* ============================= Screen 3 ================================== */

const STATES = [
  "Aberdeen City",
  "Aberdeenshire",
  "Anglesey",
  "Antrim",
  "Armagh",
];

function ScreenStatePicker({
  onApply,
  onClose,
}: {
  onApply: (s: string) => void;
  onClose: () => void;
}) {
  const [sel, setSel] = useState("Anglesey");
  return (
    <div style={{ ...col, flex: 1, justifyContent: "flex-end" }}>
      <div style={{ flex: 1, background: "rgba(15,23,42,0.35)" }} onClick={onClose} />
      <div
        style={{
          background: C.bg,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: "20px 20px 28px",
          ...col,
          gap: 16,
        }}
      >
        <div style={{ ...row, justifyContent: "space-between" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.fg }}>Select State Province</div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: C.fg }}
          >
            <X size={18} />
          </button>
        </div>
        <div style={{ ...col, position: "relative", height: 220 }}>
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              height: 44,
              transform: "translateY(-50%)",
              borderTop: `1px solid ${C.border}`,
              borderBottom: `1px solid ${C.border}`,
              pointerEvents: "none",
            }}
          />
          <div style={{ ...col, gap: 0, alignItems: "center", justifyContent: "center", height: "100%" }}>
            {STATES.map((s) => {
              const isSel = s === sel;
              return (
                <button
                  key={s}
                  onClick={() => setSel(s)}
                  style={{
                    height: 44,
                    background: "transparent",
                    border: "none",
                    fontSize: isSel ? 17 : 15,
                    fontWeight: isSel ? 700 : 500,
                    color: isSel ? C.primary : C.mutedFg,
                    cursor: "pointer",
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
        <PrimaryButton onClick={() => onApply(sel)} icon={<Check size={16} />}>
          Apply
        </PrimaryButton>
      </div>
    </div>
  );
}

/* ============================= Screen 4 ================================== */

const ADDRESS_SUGGESTIONS = [
  "E7158 Whitechapel High St",
  "E7157 Whitechapel High St",
  "E7156 Whitechapel High St",
];

function ScreenEditAddress({
  onBack,
  onPick,
}: {
  onBack: () => void;
  onPick: (a: string) => void;
}) {
  const [query, setQuery] = useState(ADDRESS_SUGGESTIONS[0]);
  const [open, setOpen] = useState(true);
  return (
    <div style={{ ...col, flex: 1 }}>
      <MiniHeader title="Edit Address" onBack={onBack} right={<MoreVertical size={18} />} />
      <div style={{ padding: "0 16px", position: "relative", zIndex: 2 }}>
        <button
          onClick={() => setOpen((o) => !o)}
          style={{
            ...row,
            gap: 8,
            height: 44,
            width: "100%",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: C.bg,
            padding: "0 12px",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <span style={{ flex: 1, fontSize: 14, color: C.fg }}>{query}</span>
          <ChevronDown size={16} color={C.mutedFg} />
        </button>
        {open ? (
          <div
            style={{
              position: "absolute",
              left: 16,
              right: 16,
              top: 50,
              background: C.bg,
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            {ADDRESS_SUGGESTIONS.map((a) => (
              <button
                key={a}
                onClick={() => {
                  setQuery(a);
                  setOpen(false);
                  onPick(a);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "12px 14px",
                  fontSize: 13.5,
                  color: C.fg,
                  background: a === query ? C.primarySoft : C.bg,
                  border: "none",
                  borderBottom: `1px solid ${C.border}`,
                  cursor: "pointer",
                }}
              >
                {a}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* Map mock */}
      <div
        style={{
          marginTop: 12,
          flex: 1,
          position: "relative",
          background:
            "linear-gradient(180deg,#eaf3ee 0%, #dbe9e1 40%, #cfe3d7 100%)",
          overflow: "hidden",
        }}
      >
        <svg width="100%" height="100%" style={{ position: "absolute", inset: 0 }}>
          <path d="M0 220 C 80 160, 160 260, 260 200 L 320 220 L 320 280 L 0 280 Z" fill="#a9d0f5" opacity="0.6" />
          <path d="M40 60 L 80 80 L 120 60 L 160 90 L 220 60 L 280 80" stroke="#b9c6d0" strokeWidth="6" fill="none" />
          <path d="M0 340 L 360 340" stroke="#fff" strokeWidth="4" />
        </svg>
        <div style={{ position: "absolute", top: 16, right: 16, ...col, gap: 8 }}>
          <button style={miniMapBtn}><Plus size={14} /></button>
          <button style={miniMapBtn}><Minus size={14} /></button>
        </div>
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "45%",
            transform: "translate(-50%, -100%)",
            color: C.destructive,
          }}
        >
          <MapPin size={36} fill="currentColor" stroke="white" strokeWidth={1.5} />
        </div>
        <div style={{ position: "absolute", bottom: 8, left: 12, fontSize: 11, color: "#5a6770" }}>
          Google
        </div>
      </div>
    </div>
  );
}

const miniMapBtn: CSSProperties = {
  ...grid,
  height: 28,
  width: 28,
  borderRadius: 6,
  background: C.bg,
  border: `1px solid ${C.border}`,
  color: C.fg,
  cursor: "pointer",
};

/* ============================= Screen 5 ================================== */

function ScreenAvatar({
  avatar,
  premadeAvatars,
  isSaving,
  error,
  onUpload,
  onSkip,
  onPremade,
  onContinue,
  onBack,
}: {
  avatar: string;
  premadeAvatars: string[];
  isSaving: boolean;
  error?: string;
  onUpload: () => void;
  onSkip: () => void;
  onPremade: (avatarValue: string) => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  return (
    <div style={{ ...col, flex: 1 }}>
      <MiniHeader onBack={onBack} />
      <div style={{ ...col, flex: 1, justifyContent: "center", padding: "0 24px", gap: 28 }}>
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: C.fg, margin: 0, lineHeight: 1.25 }}>
            Set Up Your Profile
            <br />
            Avatar or Image
          </h1>
          <div style={{ marginTop: 24 }}>
            {avatar ? (
              <img
                src={avatar}
                alt="Profile avatar"
                style={{ height: 92, width: 92, objectFit: "cover", borderRadius: 9999, border: `2px solid ${C.primarySoft}` }}
              />
            ) : (
              <AvatarBadge size={72} />
            )}
          </div>
        </div>
        <div style={{ ...row, flexWrap: "wrap", justifyContent: "center", gap: 10 }}>
          {premadeAvatars.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onPremade(item)}
              style={{
                borderRadius: 9999,
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <img src={item} alt="Premade avatar" style={{ height: 48, width: 48, borderRadius: 9999 }} />
            </button>
          ))}
        </div>
        {error ? <p style={{ margin: "0", textAlign: "center", fontSize: 12.5, color: C.destructive }}>{error}</p> : null}
        <div style={{ ...col, gap: 12 }}>
          <PrimaryButton onClick={onUpload} icon={<Upload size={16} />}>
            {isSaving ? "Uploading..." : "Upload Image"}
          </PrimaryButton>
          <OutlineButton onClick={onContinue} icon={null}>
            Continue
          </OutlineButton>
          <OutlineButton onClick={() => onPremade(premadeAvatars[0])} icon={null}>
            Use Premade Avatar
          </OutlineButton>
          <button
            onClick={onSkip}
            style={{
              ...row,
              justifyContent: "center",
              gap: 6,
              background: "transparent",
              border: "none",
              color: C.destructive,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              padding: "8px 0",
            }}
          >
            <X size={14} /> Skip this step
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================= Screen 6 ================================== */

function ScreenUploading({ onDone }: { onDone: () => void }) {
  const [p, setP] = useState(8);
  useEffect(() => {
    const t = setInterval(() => {
      setP((v) => {
        if (v >= 100) {
          clearInterval(t);
          setTimeout(onDone, 400);
          return 100;
        }
        return v + 4;
      });
    }, 90);
    return () => clearInterval(t);
  }, [onDone]);

  const size = 180;
  const r = 78;
  const circ = 2 * Math.PI * r;
  const dash = (p / 100) * circ;

  return (
    <div style={{ ...col, flex: 1 }}>
      <MiniHeader />
      <div style={{ ...col, flex: 1, justifyContent: "center", alignItems: "center", gap: 22, padding: 24 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke={C.muted} strokeWidth={10} fill="none" />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={C.primary}
            strokeWidth={10}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circ - dash}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <div style={{ ...col, alignItems: "center", gap: 4 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.fg }}>Uploading Image...</div>
          <div style={{ fontSize: 13, color: C.mutedFg }}>profile_picture.jpeg</div>
        </div>
      </div>
    </div>
  );
}

/* ============================= Screen 7 ================================== */

function ScreenPhoneOTP({
  phone,
  setPhone,
  phoneCountryIso,
  setPhoneCountryIso,
  phoneError,
  onSend,
  onBack,
}: {
  phone: string;
  setPhone: (v: string) => void;
  phoneCountryIso: string;
  setPhoneCountryIso: (v: string) => void;
  phoneError?: string;
  onSend: () => void;
  onBack: () => void;
}) {
  const selectedCountry = PHONE_COUNTRY_OPTIONS.find((c) => c.iso === phoneCountryIso) || PHONE_COUNTRY_OPTIONS[0];
  return (
    <div style={{ ...col, flex: 1 }}>
      <MiniHeader onBack={onBack} />
      <div style={{ ...col, flex: 1, padding: "0 24px", gap: 24 }}>
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.fg, margin: 0, lineHeight: 1.2 }}>
            Phone OTP Setup
          </h1>
          <p style={{ marginTop: 12, fontSize: 13, color: C.mutedFg, lineHeight: 1.45 }}>
            We'll send a one time SMS message. Carrier
            <br />
            rates may apply on your location.
          </p>
        </div>
        <div
          style={{
            ...row,
            gap: 8,
            height: 52,
            borderRadius: 9999,
            border: `1px solid ${C.border}`,
            padding: "0 16px",
            background: C.bg,
          }}
        >
          <select
            value={phoneCountryIso}
            onChange={(e) => setPhoneCountryIso(e.target.value)}
            style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, color: C.fg, maxWidth: 110 }}
          >
            {PHONE_COUNTRY_OPTIONS.map((c) => (
              <option key={c.iso} value={c.iso}>{`${c.flag} ${c.dial}`}</option>
            ))}
          </select>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={`${selectedCountry.dial} (phone number)`}
            style={{
              flex: 1,
              height: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 14,
              color: C.fg,
            }}
          />
          <HelpCircle size={16} color={C.mutedFg} />
        </div>
        <ValidationText message={phoneError} />
        <div style={{ paddingBottom: 24 }}>
          <PrimaryButton onClick={onSend}>Send OTP Code</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

/* ============================= Screen 8 ================================== */

function ScreenVerifyOTP({
  onContinue,
  onResend,
  onEmail,
  onBack,
  error,
  onTryError,
}: {
  onContinue: () => void;
  onResend: () => void;
  onEmail: () => void;
  onBack: () => void;
  error: boolean;
  onTryError: () => void;
}) {
  const [digits, setDigits] = useState<string[]>(
    error ? ["1", "8", "7", "9", "2", "5"] : ["", "", "", "", "", ""],
  );
  const filled = digits.every((d) => d !== "");
  return (
    <div style={{ ...col, flex: 1 }}>
      <MiniHeader onBack={onBack} />
      {error ? (
        <div style={{ padding: "0 16px" }}>
          <div
            style={{
              ...row,
              justifyContent: "space-between",
              gap: 8,
              borderRadius: 10,
              border: `1px solid #f4c2c7`,
              background: "#fdecee",
              color: C.destructive,
              padding: "10px 12px",
              fontSize: 12.5,
              fontWeight: 500,
            }}
          >
            <span style={{ ...row, gap: 8 }}>
              <AlertCircle size={16} />
              Passcode incorrect! Try again in 20s
            </span>
            <X size={14} />
          </div>
        </div>
      ) : null}
      <div style={{ ...col, flex: 1, padding: "16px 24px", gap: 24 }}>
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: C.fg, margin: 0 }}>
            Verify your Passcode
          </h1>
        </div>
        <div style={{ ...row, justifyContent: "center", gap: 8 }}>
          {digits.map((d, i) => (
            <input
              key={i}
              value={d}
              maxLength={1}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "");
                const next = [...digits];
                next[i] = v;
                setDigits(next);
              }}
              style={{
                height: 58,
                width: 44,
                borderRadius: 14,
                border: `1px solid ${error ? C.destructive : C.border}`,
                background: C.bg,
                textAlign: "center",
                fontSize: 22,
                fontWeight: 700,
                color: d ? C.fg : "#cdd5db",
                outline: "none",
              }}
            />
          ))}
        </div>
        <p style={{ textAlign: "center", fontSize: 12.5, color: C.mutedFg, margin: 0, lineHeight: 1.45 }}>
          We've sent a code to ••9704. Please enter it
          <br />
          here to verify your identity.
        </p>
        <button
          onClick={error ? onTryError : onResend}
          style={{
            background: "transparent",
            border: "none",
            color: C.primary,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Didn't Receive? Re-Send OTP
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ ...col, gap: 12, paddingBottom: 16 }}>
          <PrimaryButton onClick={filled ? onContinue : onTryError} disabled={!filled && !error}>
            Continue
          </PrimaryButton>
          <OutlineButton onClick={onEmail} icon={<Mail size={16} />}>
            Email the code instead
          </OutlineButton>
        </div>
      </div>
    </div>
  );
}

/* ============================== Container ================================ */

type Step =
  | "intro"
  | "identity"
  | "state"
  | "address"
  | "avatar"
  | "uploading"
  | "phone"
  | "otp"
  | "otpError";

export default function ProfileSetup() {
  const navigate = useNavigate();
  const { fetchProfile, refreshCompletionFlags } = (useAuth() || {}) as any;
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null);
  const [step, setStep] = useState<Step>("intro");
  const [identity, setIdentity] = useState<Identity>({
    fullName: "",
    gender: "",
    idCard: "",
    dob: "",
    phone: "",
    phoneCountryIso: "US",
    countryIso: "US",
    nationality: "USA",
    street: "2216 Elementary Street",
  });
  const [phone, setPhone] = useState("");
  const [selectedProvince, setSelectedProvince] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [avatar, setAvatar] = useState("");
  const [avatarError, setAvatarError] = useState("");
  const [isAvatarSaving, setIsAvatarSaving] = useState(false);
  const [identityErrors, setIdentityErrors] = useState<IdentityFieldErrors>({});
  const [phoneError, setPhoneError] = useState("");

  const parseDob = (value: string) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return null;

    const slashMatch = trimmed.match(/^(\d{1,2})\s*\/\s*(\d{1,2})\s*\/\s*(\d{4})$/);
    if (slashMatch) {
      const day = Number(slashMatch[1]);
      const month = Number(slashMatch[2]);
      const year = Number(slashMatch[3]);
      const date = new Date(Date.UTC(year, month - 1, day));
      if (!Number.isNaN(date.getTime())) return date.toISOString();
    }

    const direct = new Date(trimmed);
    return Number.isNaN(direct.getTime()) ? null : direct.toISOString();
  };

  const getSelectedCountry = (iso?: string) => {
    return PHONE_COUNTRY_OPTIONS.find((c) => c.iso === iso) || PHONE_COUNTRY_OPTIONS[0];
  };

  const normalizePhone = (value: string, countryIso: string) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const country = getSelectedCountry(countryIso);
    if (raw.startsWith("+")) return raw.replace(/[^\d+]/g, "");
    const local = raw.replace(/\D/g, "").replace(/^0+/, "");
    return `${country.dial}${local}`;
  };

  const splitStoredPhone = (value: string) => {
    const normalized = String(value || "").replace(/[^\d+]/g, "");
    const country = PHONE_COUNTRY_OPTIONS
      .slice()
      .sort((a, b) => b.dial.length - a.dial.length)
      .find((c) => normalized.startsWith(c.dial));

    if (!country) {
      return { countryIso: "US", local: normalized.replace(/^\+/, "") };
    }

    return {
      countryIso: country.iso,
      local: normalized.slice(country.dial.length),
    };
  };

  const buildProfilePayload = (phoneInput: string) => ({
    fullName: identity.fullName.trim(),
    gender: identity.gender,
    dob: parseDob(identity.dob),
    phone: normalizePhone(phoneInput || identity.phone, identity.phoneCountryIso),
    address: identity.street,
    city: identity.nationality,
    province: selectedProvince,
  });

  const validateIdentity = () => {
    const errors: IdentityFieldErrors = {};
    const fullName = String(identity.fullName || "").trim();
    const phoneDigits = String(identity.phone || "").replace(/\D/g, "");
    const dobValue = parseDob(identity.dob);

    if (!fullName || fullName.length < 2) {
      errors.fullName = "Enter your full name.";
    }

    if (!identity.gender) {
      errors.gender = "Select your gender.";
    }

    if (!identity.countryIso) {
      errors.country = "Select your nationality.";
    }

    if (!dobValue) {
      errors.dob = "Enter a valid date of birth.";
    } else {
      const dobDate = new Date(dobValue);
      const now = new Date();
      let age = now.getFullYear() - dobDate.getFullYear();
      const birthdayPassed =
        now.getMonth() > dobDate.getMonth() ||
        (now.getMonth() === dobDate.getMonth() && now.getDate() >= dobDate.getDate());
      if (!birthdayPassed) age -= 1;
      if (age < 13 || age > 120) {
        errors.dob = "Age must be between 13 and 120 years.";
      }
    }

    if (phoneDigits.length < 7 || phoneDigits.length > 14) {
      errors.phone = "Enter a valid phone number.";
    }

    setIdentityErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePhoneOnly = () => {
    const digits = String(phone || "").replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 14) {
      setPhoneError("Enter a valid phone number.");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Could not read selected image file."));
      reader.readAsDataURL(file);
    });

  const saveAvatar = async (avatarValue: string) => {
    if (!avatarValue) return false;

    setIsAvatarSaving(true);
    setAvatarError("");
    try {
      await api.patch("/api/profile/avatar", { avatar: avatarValue });
      setAvatar(avatarValue);
      localStorage.setItem("HAS_PROFILE", "true");
      await Promise.allSettled([
        typeof fetchProfile === "function" ? fetchProfile() : Promise.resolve(),
        typeof refreshCompletionFlags === "function" ? refreshCompletionFlags() : Promise.resolve(),
      ]);
      return true;
    } catch (err: any) {
      setAvatarError(err?.response?.data?.message || "We couldn't save your avatar. Please try again.");
      return false;
    } finally {
      setIsAvatarSaving(false);
    }
  };

  const handleUploadAvatarClick = () => {
    setAvatarError("");
    avatarFileInputRef.current?.click();
  };

  const handleAvatarFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setAvatarError("Please select an image file.");
      event.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAvatarError("Image is too large. Please choose a file under 5MB.");
      event.target.value = "";
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      await saveAvatar(dataUrl);
    } catch (err: any) {
      setAvatarError(err?.message || "Failed to process selected image.");
    } finally {
      event.target.value = "";
    }
  };

  const saveProfile = async (phoneInput: string) => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const payload = buildProfilePayload(phoneInput);
      if (!payload.fullName) {
        throw new Error("Please enter your full name to continue.");
      }

      await api.post("/api/profile", payload);
      localStorage.setItem("HAS_PROFILE", "true");
      await Promise.allSettled([
        typeof fetchProfile === "function" ? fetchProfile() : Promise.resolve(),
        typeof refreshCompletionFlags === "function" ? refreshCompletionFlags() : Promise.resolve(),
      ]);
      return true;
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || err?.message || "We couldn't save your profile. Please try again.");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      try {
        const res = await api.get("/api/profile");
        const p = res?.data?.profile;
        if (!active || !p) return;

        setIdentity((prev) => ({
          ...prev,
          fullName: p.fullName || prev.fullName,
          gender: p.gender || prev.gender,
          dob: p.dob ? new Date(p.dob).toISOString().slice(0, 10) : prev.dob,
          phone: splitStoredPhone(p.phone || "").local || prev.phone,
          phoneCountryIso: splitStoredPhone(p.phone || "").countryIso || prev.phoneCountryIso,
          countryIso: prev.countryIso || "US",
          nationality: p.city || prev.nationality,
          street: p.address || prev.street,
        }));
        setAvatar(p.avatar || "");
        setPhone(splitStoredPhone(p.phone || "").local || "");
        setSelectedProvince(p.province || "");
      } catch (_) {
        // Profile may not exist yet for first-time users.
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadProfile();
    return () => {
      active = false;
    };
  }, []);

  const handleIdentityContinue = async () => {
    if (!validateIdentity()) return;
    const ok = await saveProfile(identity.phone);
    if (ok) setStep("avatar");
  };

  const handleSendOtp = async () => {
    if (!validatePhoneOnly()) {
      return;
    }
    const fullPhone = normalizePhone(phone, identity.phoneCountryIso);
    if (!fullPhone) return;
    setSubmitError("");
    setStep("otp");
  };

  const handleCompleteSetup = async () => {
    const ok = await saveProfile(phone || identity.phone);
    if (!ok) return;

    await api.post("/api/activities/add-activity", {
      message: "Completed profile setup",
    }).catch(() => {});
    navigate("/health-assessment");
  };

  const screen = useMemo(() => {
    switch (step) {
      case "intro":
        return <ScreenIntro onNext={() => setStep("identity")} onHelp={() => setStep("identity")} />;
      case "identity":
        return (
          <ScreenIdentity
            data={identity}
            setData={setIdentity}
            province={selectedProvince}
            setProvince={setSelectedProvince}
            errors={identityErrors}
            onNext={handleIdentityContinue}
            onState={() => setStep("state")}
            onAddress={() => setStep("address")}
            onAvatar={() => setStep("avatar")}
          />
        );
      case "state":
        return (
          <ScreenStatePicker
            onClose={() => setStep("identity")}
            onApply={(state) => {
              setSelectedProvince(state);
              setStep("identity");
            }}
          />
        );
      case "address":
        return (
          <ScreenEditAddress
            onBack={() => setStep("identity")}
            onPick={(a) => {
              setIdentity({ ...identity, street: a });
            }}
          />
        );
      case "avatar":
        return (
          <ScreenAvatar
            avatar={avatar}
            premadeAvatars={PREMADE_AVATARS}
            isSaving={isAvatarSaving}
            error={avatarError}
            onBack={() => setStep("identity")}
            onUpload={handleUploadAvatarClick}
            onPremade={async (value) => {
              const ok = await saveAvatar(value);
              if (ok) setAvatarError("");
            }}
            onContinue={() => setStep("phone")}
            onSkip={() => setStep("phone")}
          />
        );
      case "uploading":
        return <ScreenUploading onDone={() => setStep("phone")} />;
      case "phone":
        return (
          <ScreenPhoneOTP
            phone={phone}
            setPhone={(v) => {
              setPhone(v);
              setIdentity({ ...identity, phone: v });
              setPhoneError("");
            }}
            phoneCountryIso={identity.phoneCountryIso}
            setPhoneCountryIso={(nextIso) => {
              const next = getSelectedCountry(nextIso);
              setIdentity({ ...identity, phoneCountryIso: next.iso });
            }}
            phoneError={phoneError}
            onSend={handleSendOtp}
            onBack={() => setStep("avatar")}
          />
        );
      case "otp":
        return (
          <ScreenVerifyOTP
            onBack={() => setStep("phone")}
            onContinue={handleCompleteSetup}
            onResend={() => setStep("otp")}
            onEmail={() => setStep("phone")}
            onTryError={() => setStep("otpError")}
            error={false}
          />
        );
      case "otpError":
        return (
          <ScreenVerifyOTP
            key="err"
            onBack={() => setStep("phone")}
            onContinue={handleCompleteSetup}
            onResend={() => setStep("otp")}
            onEmail={() => setStep("phone")}
            onTryError={() => setStep("otp")}
            error
          />
        );
    }
  }, [step, identity, phone, handleCompleteSetup, handleIdentityContinue]);

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        background: C.shellBg,
        ...row,
        justifyContent: "center",
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        style={{
          position: "relative",
          ...col,
          width: "100%",
          maxWidth: 420,
          minHeight: "100vh",
          overflow: "hidden",
          background: "var(--bg)",
          boxShadow: "0 20px 25px rgba(0,0,0,0.1)",
        }}
      >
        <input
          ref={avatarFileInputRef}
          type="file"
          accept="image/*"
          onChange={handleAvatarFileSelected}
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
          }}
        />

        {isLoading && (
          <div style={{ padding: "10px 16px", background: C.primarySoft, color: C.primary, fontSize: 13, fontWeight: 500 }}>
            Loading your profile...
          </div>
        )}

        {submitError && (
          <div style={{ padding: "10px 16px", background: "#fff1f2", color: "#9f1239", fontSize: 13, borderBottom: "1px solid #fecdd3" }}>
            {submitError}
          </div>
        )}

        {isSubmitting && (
          <div style={{ padding: "8px 16px", background: C.muted, color: C.mutedFg, fontSize: 12.5 }}>
            Saving your profile...
          </div>
        )}

        {screen}
      </div>
    </div>
  );
}